// AppSidebar.tsx
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/utils/utils";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard,
    Users,
    Calculator,
    BarChart3,
    FileText,
    Moon,
    Sun,
    ChevronLeft,
    ChevronRight,
    LockIcon,
    LogOut,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface AppSidebarProps {
    collapsed: boolean;
    onToggle: () => void;
}

const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Главная" },
    { to: "/people", icon: Users, label: "Клиенты" },
    { to: "/calculator", icon: Calculator, label: "Калькулятор" },
    { to: "/analytics", icon: BarChart3, label: "Аналитика" },
    { to: "/settings", icon: FileText, label: "Настройки" },
    { to: "/employees", icon: LockIcon, label: "Доступ" },
];

export function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
    const location = useLocation();
    const { theme, toggleTheme } = useTheme();
    const { logout } = useAuth();

    return (
        <aside
            className={cn(
                "fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300 ease-in-out flex flex-col",
                "print:hidden", // не печатать сайдбар
                collapsed ? "w-16" : "w-64"
            )}
        >
            {/* Logo */}
            <div className="h-16 flex items-center justify-between px-4 border-b border-sidebar-border">
                {!collapsed && (
                    <span className="font-semibold text-lg text-sidebar-foreground">CRM</span>
                )}

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onToggle}
                    className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
                    aria-label="Toggle sidebar"
                >
                    {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </Button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-4 px-2 space-y-1">
                {navItems.map((item) => {
                    const isActive = location.pathname === item.to;
                    const Icon = item.icon;

                    const link = (
                        <NavLink
                            to={item.to}
                            className={cn(
                                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                                isActive
                                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                                    : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                            )}
                        >
                            <Icon className="h-5 w-5 flex-shrink-0" />
                            {!collapsed && <span>{item.label}</span>}
                        </NavLink>
                    );

                    if (collapsed) {
                        return (
                            <Tooltip key={item.to} delayDuration={0}>
                                <TooltipTrigger asChild>{link}</TooltipTrigger>
                                <TooltipContent side="right" className="font-medium">
                                    {item.label}
                                </TooltipContent>
                            </Tooltip>
                        );
                    }

                    return <div key={item.to}>{link}</div>;
                })}
            </nav>

            {/* Footer */}
            <div className="p-3 border-t border-sidebar-border space-y-1">
                {/* Theme toggle */}
                <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size={collapsed ? "icon" : "default"}
                            onClick={toggleTheme}
                            className={cn(
                                "w-full text-sidebar-foreground hover:bg-sidebar-accent",
                                collapsed ? "justify-center" : "justify-start gap-3"
                            )}
                            aria-label="Toggle theme"
                        >
                            {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                            {!collapsed && (theme === "light" ? "Тёмная тема" : "Светлая тема")}
                        </Button>
                    </TooltipTrigger>
                    {collapsed && (
                        <TooltipContent side="right" className="font-medium">
                            {theme === "light" ? "Тёмная тема" : "Светлая тема"}
                        </TooltipContent>
                    )}
                </Tooltip>

                {/* Logout */}
                <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            onClick={logout}
                            className={cn(
                                "w-full text-destructive hover:bg-destructive/10",
                                collapsed ? "justify-center" : "justify-start gap-3"
                            )}
                            aria-label="Logout"
                        >
                            <LogOut className="h-5 w-5" />
                            {!collapsed && "Выйти"}
                        </Button>
                    </TooltipTrigger>
                    {collapsed && (
                        <TooltipContent side="right" className="font-medium">
                            Выйти
                        </TooltipContent>
                    )}
                </Tooltip>
            </div>
        </aside>
    );
}
